import { create } from "zustand";

export const copy = create((set) => ({}));

/**
 * Convert CommonMark "soft line breaks" (literal `\n` characters) inside
 * DatoCMS markdown HTML output into `<br>` tags so that single newlines
 * typed in the CMS are visually preserved.
 *
 * Newlines that separate block-level elements (e.g. between `</p>` and `<p>`)
 * are left untouched to avoid producing extra spacing or invalid HTML.
 */
const BLOCK_TAGS =
  "p|h[1-6]|div|ul|ol|li|blockquote|pre|hr|br|table|tr|td|th|thead|tbody|tfoot|section|article|aside|header|footer|nav|main|figure|figcaption|address";

const SOFT_BREAK_RE = new RegExp(
  `\\r?\\n(?!\\s*<\\/?(?:${BLOCK_TAGS})\\b)`,
  "g"
);

export function preserveSoftLineBreaks(html) {
  if (typeof html !== "string" || !html) return html;
  return html.replace(SOFT_BREAK_RE, "<br>");
}

/**
 * Split a DatoCMS markdown HTML string into the inner HTML of each `<p>` block.
 * Returns an empty array for falsy / non-string input.
 */
export function splitParagraphs(html) {
  if (typeof html !== "string" || !html) return [];
  const matches = html.matchAll(/<p\b[^>]*>([\s\S]*?)<\/p>/gi);
  return Array.from(matches, (m) => m[1].trim()).filter(Boolean);
}

// export function fetchProject(projectId) {
//   //
// }

// export function fetchAllCopy() {
//   return new Promise((resolve) => {
//     performRequest({
//       query: ALL_COPY_QUERY,
//     }).then((response, reject) => {
//       copy.setState({
//         ...response.data,
//       });
//       resolve(true);
//     });
//   });
// }

export const PROJECTS_HOME_QUERY = /*graphql*/ `
{
  allProjects(filter: { featured: { eq: true } }) {
    id
    title
    deliverables {
      id
      title
    }
    slug
    year

    featured
    featuredOrder
    featuredImage {
      url
      alt
      video {
        streamingUrl
        muxPlaybackId
        mp4Url 
        thumbnailUrl
      }
    }
  }
}
`;

export const PROJECTS_PAGE_QUERY = /*graphql*/ `
{
  allProjects(orderBy: [id_ASC]) {
    id
    title
    deliverables {
      id
      title
    }
    slug
    year
    coverImage {
        width
        height
        url
        alt
      }
      mobileCoverImage {
        width
        height
        url
        alt
      }
    featuredOrder
    featuredImage {
      url
      alt

      video {
        streamingUrl
        muxPlaybackId
        mp4Url 
        thumbnailUrl
      }
    }
  }
}
`;

export const PROJECT_QUERY = /*graphql*/ `
  query Project($slug: String) {
    project(filter: { slug: { eq: $slug } }) {
      __typename
      coverImage {
        width
        height
        url
        alt
        video {
          streamingUrl
          muxPlaybackId
          mp4Url 
          thumbnailUrl
        }
      }
      mobileCoverImage {
        width
        height
        url
        alt
        video {
          streamingUrl
          muxPlaybackId
          mp4Url 
          thumbnailUrl
        }
      }
      title
      year
      content {
        ... on ProjectheaderRecord {
          __typename
          paragraph(markdown: false)
          paragraphHeader
          bigTitle
          sectionTitle
        }
        ... on ColumnrowRecord {
          __typename
          columns {
            ... on ImagecolumnRecord {
              __typename
              image {
                width
                height
                url
                alt
                video {
                  streamingUrl
                  mp4Url
                  thumbnailUrl
                }
              }
              width
              mobileWidth
            }
            ... on EmptycolumnRecord {
                __typename
                width
                mobileWidth
            }
            ... on TextcolumnRecord {
              __typename
              width
              mobileWidth
              text(markdown: false)
              align
              cta {
                text
                url
                isExternal
              }
            }
          }
        }
        ... on ProjectnumberRecord {
          numbers {
            number
            text
          }
          titleLeft
          titleRight
          __typename
        }
        ... on ParagraphRecord {
          content(markdown: false)
          __typename
        }
      }
    }
  }
`;

export const PROJECT_NEXT_QUERY = /*graphql*/ `
  query Project {
     allProjects(orderBy: [id_ASC]) {
      coverImage {
        url
        alt
        video {
          streamingUrl
          muxPlaybackId
          mp4Url 
          thumbnailUrl
        }
      }
      mobileCoverImage {
        url
        alt
      }
      title
      slug
    }
  }
`;

export const PAGE_QUERY = /*graphql*/ `
  query Page($slug: String) {
    page(filter: { slug: { eq: $slug } }) {
      __typename
      title
      slug
      coverImage {
        width
        height
        url
        alt
        video {
          streamingUrl
          muxPlaybackId
          mp4Url
          thumbnailUrl
        }
      }
      mobileCoverImage {
        width
        height
        url
        alt
        video {
          streamingUrl
          muxPlaybackId
          mp4Url
          thumbnailUrl
        }
      }
      content {
        ... on ProjectheaderRecord {
          __typename
          paragraph(markdown: false)
          paragraphHeader
          bigTitle
          sectionTitle
        }
        ... on PageheaderRecord {
          __typename
          sectionTitle
          title
          titleBottom
          paragraph(markdown: true)
          ctaText
          ctaLink
        }
        ... on CtasectionRecord {
          __typename
          title(markdown: true)
          ctaText
          ctaLink
        }
        ... on FormSectionRecord {
          __typename
          title(markdown: true)
          subtitle
          ctaText
          successTitle
          successMessage(markdown: true)
          formName
          formFields {
            label
            name
            fieldType
            required
            options
            placeholder
            width
          }
        }
        ... on ColumnrowRecord {
          __typename
          columns {
            ... on ImagecolumnRecord {
              __typename
              image {
                width
                height
                url
                alt
                video {
                  streamingUrl
                  mp4Url
                  thumbnailUrl
                }
              }
              width
              mobileWidth
            }
            ... on EmptycolumnRecord {
                __typename
                width
                mobileWidth
            }
            ... on TextcolumnRecord {
              __typename
              width
              mobileWidth
              text(markdown: false)
              align
              cta {
                text
                url
                isExternal
              }
            }
          }
        }
        ... on ProjectnumberRecord {
          numbers {
            number
            text
          }
          titleLeft
          titleRight
          __typename
        }
        ... on ParagraphRecord {
          content(markdown: false)
          __typename
        }
      }
    }
  }
`;

export const ALL_SERVICES_QUERY = /*graphql*/ `
  query AllServices {
    allServices {
      id
      slug
      title
    }
  }
`;

export const SERVICE_QUERY = /*graphql*/ `
  query Service($slug: String) {
    service(filter: { slug: { eq: $slug } }) {
      __typename
      title
      slug
      header {
        headertext
        paragraph
        cta {
          text
          url
        }
      }
      content {
        ... on SectionlineRecord {
          __typename
          title
        }
        ... on ScrollingTitleRecord {
          __typename
          text
        }
        ... on ColumnrowRecord {
          __typename
          columns {
            ... on ImagecolumnRecord {
              __typename
              image {
                width
                height
                url
                alt
                video {
                  streamingUrl
                  mp4Url
                  thumbnailUrl
                }
              }
              mobileWidth
              width
            }
            ... on EmptycolumnRecord {
              __typename
              width
              mobileWidth
            }
            ... on TextcolumnRecord {
              __typename
              width
              mobileWidth
              text(markdown: false)
              align
              cta {
                text
                url
                isExternal
              }
            }
          }
        }
      }
    }
  }
`;
